import { useStreamEvent } from '@/services/stream';
import { trpc } from '@/trpc';
import {
  BellOutlined,
  CheckOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  ReloadOutlined,
  MessageOutlined
} from '@ant-design/icons';
import { Badge, Button, Drawer, List, Modal, Space, Tag, Tooltip, Typography, notification, Spin, Skeleton } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { history } from '@/utils/navigation';
import { MessageComposer } from '@/components/CommandPalette/MessageComposer';
import { EditOutlined } from '@ant-design/icons';
import { ThreadView } from './ThreadView';
import { useAuth } from '@/context/AuthContext';

const { Text, Paragraph } = Typography;

/**
 * Represents a notification item in the system
 */
export interface NotificationItem {
  /** Unique identifier for the notification */
  id: string;
  /** Display title of the notification */
  title: string;
  /** Notification message content */
  message: string;
  /** When the notification was created */
  timestamp: Date;
  /** Whether the user has read this notification */
  isRead: boolean;
  /** Notification type for styling and behavior */
  type: 'info' | 'success' | 'warning' | 'error';
  /** Optional URL to navigate to when clicked */
  url?: string;
  /** Optional category for grouping notifications */
  category?: string;
  /** Thread ID for grouping replies */
  threadId?: string;
  /** Number of replies in this thread */
  replyCount?: number;
  /** Number of unread messages in this thread (for merged thread display) */
  unreadCount?: number;
  /** Whether this message was sent by current user (title starts with "Message to") */
  isSentByMe?: boolean;
  /** Sender's display name (for group chat message preview) */
  senderName?: string;
  /** Names of recipients who haven't read the message yet */
  unreadRecipientNames?: string[];
}

/**
 * Props for the Notification component
 */
interface NotificationProps {
  /** Optional CSS class name for styling */
  className?: string;
}

/**
 * Data structure for incoming notification events from WebSocket
 */
interface NotificationEventData {
  /** Unique identifier for the notification event */
  id?: string;
  /** Display title of the notification */
  title?: string;
  /** Notification message content */
  message?: string;
  /** Alternative content field for notification message */
  content?: string;
  /** When the notification event was created (string or number) */
  timestamp?: string | number;
  /** Notification type for styling and behavior */
  type?: 'info' | 'success' | 'warning' | 'error';
  /** Optional URL to navigate to when clicked */
  url?: string;
  /** Optional category for grouping notifications */
  category?: string;
  /** Name of the event that triggered this notification */
  eventName?: string;
  /** Thread ID for message notifications - used to expand correct thread on click */
  threadId?: string;
}

const TOAST_DEDUP_WINDOW_MS = 3000; // Don't show duplicate toast within 3 seconds

// Type validation function for notification event data
export const validateNotificationEventData = (data: unknown): NotificationEventData | null => {
  if (!data || typeof data !== 'object') return null;

  const eventData = data as Record<string, unknown>;

  // Validate required fields and their types
  const validatedData: NotificationEventData = {
    id: typeof eventData.id === 'string' ? eventData.id : undefined,
    title: typeof eventData.title === 'string' ? eventData.title : undefined,
    message: typeof eventData.message === 'string' ? eventData.message : undefined,
    content: typeof eventData.content === 'string' ? eventData.content : undefined,
    timestamp:
      typeof eventData.timestamp === 'string' || typeof eventData.timestamp === 'number'
        ? (eventData.timestamp as string | number)
        : undefined,
    type:
      typeof eventData.type === 'string' && ['info', 'success', 'warning', 'error'].includes(eventData.type)
        ? (eventData.type as NotificationEventData['type'])
        : undefined,
    url: typeof eventData.url === 'string' ? eventData.url : undefined,
    category: typeof eventData.category === 'string' ? eventData.category : undefined,
    eventName: typeof eventData.eventName === 'string' ? eventData.eventName : undefined,
    threadId: typeof eventData.threadId === 'string' ? eventData.threadId : undefined
  };

  // Check if we have at least some valid data
  if (!validatedData.id && !validatedData.title && !validatedData.message && !validatedData.content) {
    return null;
  }

  return validatedData;
};

export const formatTimestamp = (timestamp: Date): string => {
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - timestamp.getTime()) / (1000 * 60));

  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}d ago`;

  return timestamp.toLocaleDateString();
};

export const parseHtmlMessage = (message: string) => {
  // Parse <b> tags to bold text and <br/> tags to line breaks
  const boldRegex = /<b>(.*?)<\/b>/g;
  const brRegex = /<br\s*\/?>/gi;
  const parts: Array<{ text: string; isBold: boolean; isLineBreak: boolean }> = [];
  let lastIndex = 0;
  let match;

  // First, handle <b> tags
  while ((match = boldRegex.exec(message)) !== null) {
    // Add text before the tag
    if (match.index > lastIndex) {
      const textBefore = message.slice(lastIndex, match.index);
      // Split by <br/> tags and add each part
      const segments = textBefore.split(brRegex);
      segments.forEach((segment, index) => {
        if (index > 0) {
      // Add line break
          parts.push({
            text: '',
            isBold: false,
            isLineBreak: true
          });
        }
        if (segment) {
          parts.push({
            text: segment,
            isBold: false,
            isLineBreak: false
          });
        }
      });
    }

    // Add bold text
    parts.push({
      text: match[1],
      isBold: true,
      isLineBreak: false
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < message.length) {
    const remainingText = message.slice(lastIndex);
    const segments = remainingText.split(brRegex);
    segments.forEach((segment, index) => {
      if (index > 0) {
    // Add line break
        parts.push({
          text: '',
          isBold: false,
          isLineBreak: true
        });
      }
      if (segment) {
        parts.push({
          text: segment,
          isBold: false,
          isLineBreak: false
        });
      }
    });
  }

  return parts;
};

export const renderNotificationTitle = (title: string) => {
  // Check if title follows Support Center format
  const supportCenterMatch = title.match(/Support Center:\s*([A-Z0-9-]+)\s*-\s*(.+)/);
  if (supportCenterMatch) {
    const taskId = supportCenterMatch[1];
    const action = supportCenterMatch[2];

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ color: '#1890ff', fontWeight: 600 }}>Support Center:</span>
        <Tag
          style={{
            backgroundColor: '#f0f9ff',
            color: '#1890ff',
            border: '1px solid #91d5ff',
            borderRadius: '4px',
            fontSize: '11px',
            padding: '2px 6px',
            fontWeight: 600
          }}
        >
          {taskId}
        </Tag>
        <span style={{ color: '#262626' }}>-</span>
        <span style={{ color: '#262626' }}>{action}</span>
      </div>
    );
  }

  return title;
};

/**
 * Helper function to get icon and styling based on notification type
 */
export const getTypeConfig = (type: 'info' | 'success' | 'warning' | 'error') => {
  switch (type) {
    case 'success':
      return {
        icon: <CheckOutlined style={{ color: '#52c41a', fontSize: '14px' }} />,
        backgroundColor: '#f6ffed',
        borderColor: '#b7eb8f',
        cardBgColor: '#ffffff'
      };
    case 'warning':
      return {
        icon: <WarningOutlined style={{ color: '#faad14', fontSize: '14px' }} />,
        backgroundColor: '#fffbe6',
        borderColor: '#ffe58f',
        cardBgColor: '#ffffff'
      };
    case 'error':
      return {
        icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: '14px' }} />,
        backgroundColor: '#fff2f0',
        borderColor: '#ffccc7',
        cardBgColor: '#ffffff'
      };
    case 'info':
    default:
      return {
        icon: <InfoCircleOutlined style={{ color: '#1890ff', fontSize: '14px' }} />,
        backgroundColor: '#f0f9ff',
        borderColor: '#91d5ff',
        cardBgColor: '#ffffff'
      };
  }
};

/**
 * Metadata structure for inbox messages
 */
type InboxMetadata = {
  senderId?: string;
  senderName?: string;
  recipientNames?: string;
};

const supportCenterIssueUrl = (entityType?: string | null, entityId?: string | null): string | undefined =>
  entityType === 'SUPPORT_CENTER_ISSUE' && entityId
    ? `/support-center/my-requests/${encodeURIComponent(entityId)}`
    : undefined;

/**
 * Convert server inbox item to NotificationItem format
 * @param item - Server inbox item
 * @param currentUserId - Current logged-in user's ID (for determining isSentByMe)
 */
const serverItemToNotification = (item: {
  id?: string;
  title?: string;
  message?: string;
  createdAt?: Date | string;
  isRead?: boolean;
  type?: string | null;
  url?: string | null;
  category?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  threadId?: string | null;
  replyCount?: number;
  metadata?: InboxMetadata | null;
  unreadRecipientNames?: string[];
}, currentUserId?: string): NotificationItem => {
  // Determine if message was sent by current user using metadata.senderId
  // Fall back to title-based detection for backward compatibility
  const metadata = item.metadata;
  const isSentByMe = metadata?.senderId && currentUserId
    ? metadata.senderId === currentUserId
    : item.title?.startsWith('Message to ') ?? false;
  const fallbackUrl = supportCenterIssueUrl(item.entityType, item.entityId);

  return {
    id: item.id || '',
    title: item.title || '',
    message: item.message || '',
    timestamp: new Date(item.createdAt ?? Date.now()),
    isRead: item.isRead ?? false,
    type: (item.type as NotificationItem['type']) || 'info',
    threadId: item.threadId || undefined,
    replyCount: item.replyCount ?? 0,
    url: item.url || fallbackUrl,
    category: item.category || undefined,
    isSentByMe,
    senderName: metadata?.senderName,
    unreadRecipientNames: item.unreadRecipientNames || []
  };
};

/**
 * Deduplicate notifications by threadId
 * For messages belonging to the same thread, show one card with:
 * - Title from the FIRST (root) message (e.g., "Message from X" or "Message to Y")
 * - Timestamp from the LATEST message (for sorting and display)
 * - ReplyCount from the latest message
 * This prevents showing multiple cards for the same conversation
 */
const deduplicateByThread = (notifications: NotificationItem[]): NotificationItem[] => {
  // Group messages by threadId
  const threadGroups = new Map<string, NotificationItem[]>();
  const standalone: NotificationItem[] = [];

  for (const notification of notifications) {
    if (notification.threadId) {
      const group = threadGroups.get(notification.threadId) || [];
      group.push(notification);
      threadGroups.set(notification.threadId, group);
    } else {
      standalone.push(notification);
    }
  }

  // For each thread, create a merged notification:
  // - Use the OLDEST message's title (root message title)
  // - Use the NEWEST message's timestamp (for sorting)
  // - Count unread messages in the thread
  const mergedThreads: NotificationItem[] = [];
  for (const [threadId, messages] of threadGroups) {
    // Sort by timestamp ascending to find oldest (first) and newest (last)
    const sortedMessages = [...messages].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    const oldestMessage = sortedMessages[0]; // Root message - has the original title
    const newestMessage = sortedMessages[sortedMessages.length - 1]; // Latest activity
    
    // Count unread messages in this thread
    const unreadCount = messages.filter(m => !m.isRead).length;
    // Thread is considered read only if all messages are read
    const hasUnread = unreadCount > 0;

    const rootMessage = sortedMessages.find((message) => message.id === message.threadId) || oldestMessage;
    const latestMessage = sortedMessages.reduce((latest, message) =>
      message.timestamp.getTime() >= latest.timestamp.getTime() ? message : latest,
    newestMessage);
    const latestUrl = [...sortedMessages]
      .reverse()
      .find((message) => message.url?.trim())?.url;

    mergedThreads.push({
      ...latestMessage,
      // Use the root row title because Support Center thread persistence updates it to the latest case title.
      title: rootMessage.title,
      // Keep latest timestamp for sorting
      timestamp: latestMessage.timestamp,
      // Keep replyCount from the latest message
      replyCount: latestMessage.replyCount,
      // Thread is unread if any message is unread
      isRead: !hasUnread,
      // Store unread count for display
      unreadCount,
      // Keep isSentByMe from latest message (to show unread indicator for latest message)
      isSentByMe: latestMessage.isSentByMe,
      // Keep senderName from latest message
      senderName: latestMessage.senderName,
      // Keep unreadRecipientNames from latest message
      unreadRecipientNames: latestMessage.unreadRecipientNames,
      // Keep thread navigation available when a later Support Center event was stored without a URL.
      url: latestMessage.url || latestUrl
    });
  }

  // Combine and sort by timestamp descending (newest first)
  const result = [...mergedThreads, ...standalone];
  return result.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
};

const Notification: React.FC<NotificationProps> = ({ className }) => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [composeModalVisible, setComposeModalVisible] = useState(false);
  // Don't initialize from localStorage to avoid showing wrong user's data after account switch
  // Server data is the single source of truth
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [isServerSynced, setIsServerSynced] = useState(false);
  // Track which threads are expanded (by threadId)
  const [expandedThreads, setExpandedThreads] = useState<Set<string>>(new Set());
  // Ref to access latest expandedThreads inside memoized callbacks (avoids stale closure)
  const expandedThreadsRef = useRef<Set<string>>(expandedThreads);
  useEffect(() => {
    expandedThreadsRef.current = expandedThreads;
  }, [expandedThreads]);
  // Toggle between showing active vs archived messages
  const [showArchived, setShowArchived] = useState(false);

  // Toast dedup map — tied to component lifecycle, cleaned up on unmount
  // Prevents duplicate toasts from being shown within TOAST_DEDUP_WINDOW_MS
  const recentlyShownToastsRef = useRef(new Map<string, number>());
  useEffect(() => {
    return () => {
      recentlyShownToastsRef.current.clear();
    };
  }, []);

  const [api, contextHolder] = notification.useNotification();

  const navigate = history.push;

  // tRPC utils for imperative query invalidation (used to notify ThreadView of new messages)
  const trpcUtils = trpc.useUtils();
  
  // Get current user ID for determining message ownership (memoized to avoid unnecessary re-renders)
  const { initialState } = useAuth();
  const currentUserId = useMemo(() => initialState?.currentUser?.id, [initialState?.currentUser?.id]);

  // Detect OS for keyboard shortcut display
  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  const shortcutKey = isMac ? '⌘' : 'Ctrl';

  // Toggle thread expansion
  const toggleThread = useCallback((threadId: string | undefined) => {
    if (!threadId) return;
    setExpandedThreads(prev => {
      const next = new Set(prev);
      if (next.has(threadId)) {
        next.delete(threadId);
      } else {
        next.add(threadId);
      }
      return next;
    });
  }, []);

  // Global keyboard shortcut: Cmd+I / Ctrl+I to toggle inbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input/textarea
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'i') {
        e.preventDefault();
        setDrawerVisible(prev => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Clear notifications when switching between inbox/archived view
  useEffect(() => {
    setNotifications([]);
    setIsServerSynced(false);
  }, [showArchived]);

  // Track if user has vendor access (to avoid repeated error logging and unnecessary refetches)
  const [hasVendorAccessError, setHasVendorAccessError] = useState(false);

  // tRPC queries and mutations
  const inboxQuery = trpc.inbox.list.useQuery(
    { limit: 50, isArchived: showArchived },
    {
      // Fetch on mount and refetch periodically
      refetchOnWindowFocus: !hasVendorAccessError, // Don't refetch on focus if access denied
      refetchInterval: hasVendorAccessError ? false : 5 * 60 * 1000, // Stop refetching if access denied
      staleTime: 0, // Always refetch when showArchived changes
      enabled: !hasVendorAccessError, // Disable query entirely if access denied
      retry: (failureCount, error) => {
        // Don't retry on FORBIDDEN errors (vendor access denied)
        if (error && 'data' in error && (error as { data?: { code?: string } }).data?.code === 'FORBIDDEN') {
          return false;
        }
        // Default retry behavior for other errors (3 retries)
        return failureCount < 3;
      }
    }
  );

  // Process inbox data when query succeeds or currentUserId changes
  // Using useEffect instead of onSuccess to ensure latest currentUserId is used
  useEffect(() => {
    if (inboxQuery.data) {
      const serverNotifications = inboxQuery.data.items.map(item => 
        serverItemToNotification(item, currentUserId)
      );
      
      // Deduplicate by threadId - show only the latest message per thread
      // This prevents showing multiple cards for the same conversation
      const deduplicatedNotifications = deduplicateByThread(serverNotifications);
      
      setNotifications(deduplicatedNotifications);
      setIsServerSynced(true);
      // Clear any previous vendor access error if query succeeds
      if (hasVendorAccessError) {
        setHasVendorAccessError(false);
      }
    }
  }, [inboxQuery.data, currentUserId, hasVendorAccessError]);

  // Handle query errors - differentiate between vendor access errors and other errors
  useEffect(() => {
    if (inboxQuery.error) {
      const errorCode = (inboxQuery.error as { data?: { code?: string } }).data?.code;
      
      if (errorCode === 'FORBIDDEN') {
        // Vendor access denied - this is expected for operators without portal access
        // Don't log as error to avoid console spam
        if (!hasVendorAccessError) {
          setHasVendorAccessError(true);
          // Log once at info level for debugging if needed
          console.info('[Inbox] Vendor access denied - inbox features disabled for this session');
        }
      } else {
        // Other errors - log for debugging
        console.error('Failed to fetch inbox from server:', inboxQuery.error);
      }
    }
  }, [inboxQuery.error, hasVendorAccessError]);

  const markAsReadMutation = trpc.inbox.markAsRead.useMutation({
    onSuccess: () => {
      // Refetch to sync state (inbox.list response carries unread counts)
      inboxQuery.refetch();
    }
  });

  const markAllAsReadMutation = trpc.inbox.markAllAsRead.useMutation({
    onSuccess: () => {
      inboxQuery.refetch();
    }
  });

  const markThreadAsReadMutation = trpc.inbox.markThreadAsRead.useMutation({
    onSuccess: () => {
      inboxQuery.refetch();
    }
  });

  const archiveMutation = trpc.inbox.archive.useMutation({
    onSuccess: () => {
      inboxQuery.refetch();
    }
  });

  const archiveBatchMutation = trpc.inbox.archiveBatch.useMutation({
    onSuccess: () => {
      inboxQuery.refetch();
    }
  });

  const unarchiveMutation = trpc.inbox.unarchive.useMutation({
    onSuccess: () => {
      inboxQuery.refetch();
    }
  });

  // Helper functions for URL handling
  const isAbsoluteUrl = (u: string) => /^([a-z][a-z0-9+\-.]*:)?\/\//i.test(u) || /^[a-z][a-z0-9+\-.]*:/i.test(u);
  const normalizeAppPath = (u: string) => `/${u.trim().replace(/^\/+/, '')}`;
  const navigateToUrl = (u: string) => (
    isAbsoluteUrl(u)
      ? window.open(u, '_blank', 'noopener,noreferrer')
      : navigate(normalizeAppPath(u))
  );
  
  // Check if URL is meaningful (not just root/dashboard)
  // Returns false for URLs like "/" or "http://localhost:3001/" (root paths)
  const isMeaningfulUrl = (u: string | undefined): boolean => {
    if (!u) return false;
    try {
      // For absolute URLs, check if pathname is more than just "/"
      if (isAbsoluteUrl(u)) {
        const parsed = new URL(u);
        return parsed.pathname !== '/' && parsed.pathname !== '';
      }
      // For relative paths, check if it's not just "/" or empty
      const normalized = normalizeAppPath(u);
      return normalized !== '/';
    } catch {
      return false;
    }
  };

  // Server unread counts come bundled with the inbox.list response.
  // - totalUnreadCount: for main bell icon (includes both inbox and archived)
  // - inboxUnreadCount: for Inbox tab badge
  // - archivedUnreadCount: for Archived tab badge
  const totalUnreadCount = inboxQuery.data?.unread?.total ?? notifications.filter((n) => !n.isRead).length;
  const inboxUnreadCount = inboxQuery.data?.unread?.inbox ?? 0;
  const archivedUnreadCount = inboxQuery.data?.unread?.archived ?? 0;

  // Handle incoming notification events from WebSocket
  // Strategy: Database is single source of truth - we refetch instead of adding to local state
  // This prevents duplicates caused by WebSocket ID != Database ID mismatch
  const handleIncomingNotification = useCallback(
    (data: unknown, event: string) => {
      // Validate event data with proper type checking
      const eventData = validateNotificationEventData(data);
      if (!eventData) {
        console.warn('Invalid notification event data received:', data);
        return;
      }

      const parsedMessage = eventData.message || eventData.content || 'You have a new notification';
      const notificationType = eventData.type || 'info';

      // Build notification object for toast display only (not for state)
      // threadId is now properly extracted in validateNotificationEventData
      const toastNotification = {
        title: eventData.title || 'New Notification',
        message: parsedMessage,
        type: notificationType,
        url: eventData.url,
        category: eventData.category,
        threadId: eventData.threadId
      };

      // If the drawer is open AND the thread is currently expanded (user is viewing it), auto-mark as read
      // This prevents the unread badge from flickering when new messages arrive in an open thread
      // Both conditions are required: drawer must be visible and the specific thread must be expanded
      const incomingThreadId = eventData.threadId;
      if (incomingThreadId && drawerVisible && expandedThreadsRef.current.has(incomingThreadId)) {
        // Invalidate the thread query so the mounted ThreadView auto-refetches
        // This is the centralized event dispatch — ThreadView no longer has its own WebSocket listeners
        trpcUtils.inbox.getThread.invalidate({ threadId: incomingThreadId });
        // Mark as read, but skip if a mutation is already in flight (debounce rapid WS events)
        if (!markThreadAsReadMutation.isLoading) {
          markThreadAsReadMutation.mutate(
            { threadId: incomingThreadId },
            {
              onSettled: () => {
                // Refetch after mark-as-read settles to get accurate unread counts
                inboxQuery.refetch();
              }
            }
          );
        }
      } else {
        // No expanded thread match - just refetch to show the new unread notification
        inboxQuery.refetch();
      }

      // Generate unique key for toast deduplication
      // Use threadId if available (for thread messages), otherwise use notification ID
      const toastDedupeKey = eventData.threadId || eventData.id || `toast-${Date.now()}`;
      
      // Check if we've recently shown a toast for this notification/thread
      const toastMap = recentlyShownToastsRef.current;
      const now = Date.now();
      const cutoffTime = now - TOAST_DEDUP_WINDOW_MS;

      // Proactively clean stale entries on every access (avoids unbounded growth)
      for (const [key, timestamp] of toastMap.entries()) {
        if (timestamp < cutoffTime) {
          toastMap.delete(key);
        }
      }

      const lastShownTime = toastMap.get(toastDedupeKey);
      if (lastShownTime && lastShownTime >= cutoffTime) {
        // Skip duplicate toast - already shown recently
        return;
      }
      
      // Mark this toast as shown
      toastMap.set(toastDedupeKey, now);

      // Show toast notification if drawer is not open (immediate feedback)
      if (!drawerVisible) {
        const typeConfig = getTypeConfig(toastNotification.type);

        api.open({
          key: toastDedupeKey, // Use unique key to prevent duplicate toasts from Ant Design
          message: (
            <div style={{ position: 'relative', paddingLeft: '12px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Part 1: Header with category tag */}
                {toastNotification.category && (
                  <div style={{ marginBottom: '12px' }}>
                    <Tag
                      style={{
                        fontSize: '10px',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        backgroundColor: typeConfig.backgroundColor,
                        color: typeConfig.icon.props.style.color,
                        border: `1px solid ${typeConfig.borderColor}`,
                        fontWeight: 500
                      }}
                    >
                      {toastNotification.category}
                    </Tag>
                  </div>
                )}

                {/* Part 2: Title */}
                <div style={{ fontSize: '16px', fontWeight: 600, color: '#262626', marginBottom: '8px' }}>
                  {renderNotificationTitle(toastNotification.title)}
                </div>

                {/* Part 3: Message */}
                <div
                  style={{
                    color: '#595959',
                    fontSize: '13px',
                    lineHeight: '1.5',
                    marginBottom: '8px'
                  }}
                >
                  {parseHtmlMessage(toastNotification.message).map((part, index) =>
                    part.isLineBreak ? (
                      <br key={index} />
                    ) : (
                      <span
                        key={index}
                        style={{
                          fontWeight: part.isBold ? 600 : 400,
                          color: part.isBold ? '#262626' : '#595959'
                        }}
                      >
                        {part.text}
                      </span>
                    )
                  )}
                </div>
              </div>
            </div>
          ),
          description: null,
          placement: 'topRight',
          duration: 8,
          onClick: () => {
            // Close the toast immediately after clicking
            api.destroy(toastDedupeKey);
            
            // For message notifications with threadId, open drawer and expand the thread
            if (toastNotification.threadId) {
              // Open the inbox drawer and expand the corresponding thread
              setDrawerVisible(true);
              setExpandedThreads(prev => {
                const next = new Set(prev);
                next.add(toastNotification.threadId!);
                return next;
              });
              // Also mark the thread as read
              markThreadAsReadMutation.mutate({ threadId: toastNotification.threadId });
            } else if (toastNotification.url) {
              // Navigate to URL for non-message notifications
              navigateToUrl(toastNotification.url);
            } else {
              // If no URL and no threadId, open drawer to see full notification
              setDrawerVisible(true);
            }
          },
          style: {
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: 'none',
            background: typeConfig.cardBgColor,
            maxWidth: '350px',
            minWidth: '280px',
            padding: '16px',
            position: 'relative',
            overflow: 'hidden'
          },
          icon: null
        });
      }
    },
    [drawerVisible, api, inboxQuery, markThreadAsReadMutation, trpcUtils]
  );

  // Listen for notification events - refetch on create/update to sync with database
  useStreamEvent('notification.event.created', handleIncomingNotification);
  useStreamEvent('notification.event.updated', handleIncomingNotification);
  useStreamEvent('notification.event.deleted', (data: unknown, _event: string) => {
    // Remove notification if deleted
    const eventData = data as Record<string, unknown> | null;
    if (eventData?.id) {
      const deletedId = eventData.id as string;
      setNotifications((prev) => prev.filter((n) => n.id !== deletedId));
    }
  });

  const handleMarkAsRead = useCallback(async (id: string) => {
    // Update local state immediately for better UX (optimistic update)
    setNotifications((prev) => prev.map((notification) => (notification.id === id ? { ...notification, isRead: true } : notification)));

    // Sync to server
    markAsReadMutation.mutate({ itemIds: [id] });
  }, [markAsReadMutation]);

  const handleDelete = useCallback(async (id: string, threadId: string) => {
    // Update local state immediately for better UX (optimistic update)
    setNotifications((prev) => prev.filter((notification) => notification.threadId !== threadId));

    // Archive entire thread on server (soft delete)
    archiveMutation.mutate({ itemId: id, threadId });
  }, [archiveMutation]);

  const handleRestore = useCallback(async (id: string, threadId?: string) => {
    // Update local state immediately for better UX (optimistic update)
    setNotifications((prev) => prev.filter((notification) => notification.id !== id));

    // Restore entire thread if threadId is provided, otherwise restore single item
    unarchiveMutation.mutate({ itemId: id, threadId });
  }, [unarchiveMutation]);

  const handleNotificationClick = useCallback(
    (notification: NotificationItem) => {
      // Only mark as read when clicking the card, don't navigate
      // Navigation should be done via "View Details" button only
      if (!notification.isRead) {
        handleMarkAsRead(notification.id);
      }
    },
    [handleMarkAsRead]
  );

  const handleMarkAllAsRead = useCallback(() => {
    // Optimistic update
    setNotifications((prev) => prev.map((notification) => ({ ...notification, isRead: true })));
    // Sync to server
    markAllAsReadMutation.mutate();
  }, [markAllAsReadMutation]);

  const handleClearAll = useCallback(() => {
    // Archive all notifications
    setNotifications([]);
    // Use batch API for better performance (single request instead of N requests)
    const items = notifications.map(n => ({ 
      itemId: n.id, 
      threadId: n.threadId 
    }));
    if (items.length > 0) {
      archiveBatchMutation.mutate({ items });
    }
  }, [notifications, archiveBatchMutation]);

  const handleRefresh = useCallback(() => {
    inboxQuery.refetch();
  }, [inboxQuery]);

  return (
    <div className={className}>
      {contextHolder}
      <Tooltip title={`Inbox (${shortcutKey}+I)`}>
        <Button
          type="text"
          icon={
            <Badge
              count={totalUnreadCount}
              size="small"
              style={{
                backgroundColor: totalUnreadCount > 0 ? '#ff4d4f' : '#d9d9d9',
                boxShadow: totalUnreadCount > 0 ? '0 0 0 2px rgba(255, 77, 79, 0.2)' : 'none'
              }}
            >
              <BellOutlined
                style={{
                  fontSize: '18px',
                  color: totalUnreadCount > 0 ? '#1890ff' : '#8c8c8c',
                  transition: 'all 0.3s ease'
                }}
              />
            </Badge>
          }
          onClick={() => setDrawerVisible(true)}
          style={{
            padding: '8px 12px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
            transition: 'all 0.3s ease',
            border: '1px solid #f0f0f0'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f5f5f5';
            e.currentTarget.style.borderColor = '#d9d9d9';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.borderColor = '#f0f0f0';
          }}
        />
      </Tooltip>

      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Space>
              <BellOutlined style={{ fontSize: '18px', color: showArchived ? '#8c8c8c' : '#1890ff' }} />
              <span style={{ fontSize: '16px', fontWeight: 600 }}>
                {showArchived ? 'Archived' : 'Inbox'}
              </span>
              {/* Show unread count for current view */}
              {!showArchived && inboxUnreadCount > 0 && (
                <Badge
                  count={inboxUnreadCount}
                  size="small"
                  style={{
                    backgroundColor: '#ff4d4f',
                    fontSize: '12px'
                  }}
                />
              )}
              {showArchived && archivedUnreadCount > 0 && (
                <Badge
                  count={archivedUnreadCount}
                  size="small"
                  style={{
                    backgroundColor: '#ff4d4f',
                    fontSize: '12px'
                  }}
                />
              )}
              {inboxQuery.isFetching && <Spin size="small" />}
            </Space>
            <Space>
              <Tooltip title="Send Message">
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => setComposeModalVisible(true)}
                  style={{ fontSize: '12px', color: '#1890ff' }}
                />
              </Tooltip>
              <Tooltip title="Refresh">
                <Button
                  type="text"
                  size="small"
                  icon={<ReloadOutlined spin={inboxQuery.isFetching} />}
                  onClick={handleRefresh}
                  style={{ fontSize: '12px', color: '#1890ff' }}
                />
              </Tooltip>
              {/* Toggle between inbox and archived view - show unread count */}
              <Button
                type="text"
                size="small"
                onClick={() => setShowArchived(!showArchived)}
                style={{ fontSize: '12px', color: showArchived ? '#52c41a' : '#8c8c8c' }}
              >
                {showArchived ? (
                  <>← Back to Inbox {inboxUnreadCount > 0 && <Badge count={inboxUnreadCount} size="small" style={{ marginLeft: 4 }} />}</>
                ) : (
                  <>Archived {archivedUnreadCount > 0 && <Badge count={archivedUnreadCount} size="small" style={{ marginLeft: 4 }} />}</>
                )}
              </Button>
              {/* Mark all read - only show when there are unread in current view */}
              {((!showArchived && inboxUnreadCount > 0) || (showArchived && archivedUnreadCount > 0)) && (
                <Button type="text" size="small" onClick={handleMarkAllAsRead} style={{ fontSize: '12px', color: '#1890ff' }}>
                  Mark all read
                </Button>
              )}
              {/* Clear all (archive) - only show in inbox view */}
              {!showArchived && notifications.length > 0 && (
                <Button type="text" size="small" onClick={handleClearAll} style={{ fontSize: '12px', color: '#ff4d4f' }}>
                  Clear all
                </Button>
              )}
            </Space>
          </div>
        }
        placement="right"
        width={450}
        onClose={() => {
          setDrawerVisible(false);
          // Reset expanded threads so they start collapsed on next open
          setExpandedThreads(new Set());
        }}
        open={drawerVisible}
        styles={{
          body: { padding: 0 },
          header: {
            borderBottom: '1px solid #f0f0f0',
            padding: '16px 24px'
          }
        }}
      >
        {/* Vendor access error state */}
        {hasVendorAccessError ? (
          <div
            style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: '#8c8c8c'
            }}
          >
            <ExclamationCircleOutlined
              style={{
                fontSize: '64px',
                color: '#faad14',
                marginBottom: '16px',
                display: 'block'
              }}
            />
            <Text type="secondary" style={{ fontSize: '16px', display: 'block', marginBottom: '8px' }}>
              Inbox unavailable
            </Text>
            <Text type="secondary" style={{ fontSize: '14px' }}>
              Your account may not have access to this portal. Please contact your administrator if you believe this is an error.
            </Text>
          </div>
        ) : inboxQuery.isLoading && notifications.length === 0 ? (
          /* Loading skeleton on initial load */
          <div style={{ padding: '16px' }}>
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                style={{
                  padding: '16px 20px',
                  margin: '0 16px 8px 16px',
                  borderRadius: '8px',
                  border: '1px solid #f0f0f0',
                  backgroundColor: '#ffffff'
                }}
              >
                <Skeleton active avatar={{ size: 'small', shape: 'square' }} paragraph={{ rows: 2 }} />
              </div>
            ))}
          </div>
        ) : (
        <List
          dataSource={notifications}
          loading={false}
          renderItem={(notification) => {
            return (
              <List.Item
                style={{
                  padding: '16px 20px',
                  borderBottom: '1px solid #f5f5f5',
                  backgroundColor: '#ffffff',
                  cursor: 'default',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  margin: '0 16px 8px 16px',
                  borderRadius: '8px',
                  border: '1px solid #f0f0f0',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  overflow: 'hidden'
                }}
                onClick={() => handleNotificationClick(notification)}
              >
                <div style={{ width: '100%' }}>
                  {/* Part 1: Header with icon, category, and actions */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {/* Category tag */}
                          {notification.category && (
                            <Tag
                              style={{
                                marginBottom: '0px',
                                fontSize: '10px',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                backgroundColor: getTypeConfig(notification.type).backgroundColor,
                                color: getTypeConfig(notification.type).icon.props.style.color,
                                border: `1px solid ${getTypeConfig(notification.type).borderColor}`,
                                fontWeight: 500
                              }}
                            >
                              {notification.category}
                            </Tag>
                          )}
                          {/* Unread indicator with count */}
                          {!notification.isRead && (
                            <Badge
                              count={notification.unreadCount || 1}
                              size="small"
                              style={{
                                backgroundColor: '#ff4d4f',
                                fontSize: '10px',
                                minWidth: '16px',
                                height: '16px',
                                lineHeight: '16px'
                              }}
                            />
                          )}
                        </div>

                        <Space style={{ marginLeft: '8px' }}>
                          {showArchived ? (
                            <Button
                              type="text"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRestore(notification.id, notification.threadId);
                              }}
                              style={{
                                color: '#52c41a',
                                fontSize: '12px',
                                padding: '4px 8px',
                                minWidth: 'auto',
                                borderRadius: '4px'
                              }}
                            >
                              Restore
                            </Button>
                          ) : notification.threadId ? (
                            <Tooltip title="Archive conversation">
                              <Button
                                type="text"
                                size="small"
                                icon={<CloseOutlined style={{ fontSize: '14px' }} />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // threadId is guaranteed non-null by the conditional above
                                  handleDelete(notification.id, notification.threadId!);
                                }}
                                style={{
                                  color: '#8c8c8c',
                                  padding: '4px',
                                  minWidth: 'auto',
                                  borderRadius: '4px'
                                }}
                              />
                            </Tooltip>
                          ) : null}
                        </Space>
                      </div>
                    </div>
                  </div>

                  {/* Part 2: Title */}
                  <div style={{ marginBottom: '12px' }}>
                    <Text
                      strong
                      style={{
                        color: '#262626',
                        fontSize: '16px',
                        lineHeight: '1.4',
                        display: 'block',
                        fontWeight: 600
                      }}
                    >
                      {renderNotificationTitle(notification.title)}
                    </Text>
                  </div>

                  {/* Part 3: Message content */}
                  <div style={{ marginBottom: '12px' }}>
                    <Paragraph
                      style={{
                        margin: '0 0 0 0',
                        color: '#595959',
                        fontSize: '13px',
                        lineHeight: '1.5'
                      }}
                      ellipsis={{
                        tooltip: notification.senderName
                          ? `${notification.senderName}: ${notification.message}`
                          : notification.message,
                        rows: 6
                      }}
                    >
                      {/* Show sender name before message for all conversations */}
                      {notification.senderName && (
                        <span style={{ fontWeight: 600, color: '#1890ff' }}>
                          {notification.senderName}:{' '}
                        </span>
                      )}
                      {parseHtmlMessage(notification.message).map((part, index) =>
                        part.isLineBreak ? (
                          <br key={index} />
                        ) : (
                          <span
                            key={index}
                            style={{
                              fontWeight: part.isBold ? 600 : 400,
                              color: part.isBold ? '#262626' : '#595959'
                            }}
                          >
                            {part.text}
                          </span>
                        )
                      )}
                      {/* Show unread indicator - display unread recipient names */}
                      {notification.unreadRecipientNames && notification.unreadRecipientNames.length > 0 && (
                        <span style={{ 
                          color: '#8c8c8c', 
                          fontSize: '12px',
                          marginLeft: '4px'
                        }}>
                          ({notification.unreadRecipientNames.join(', ')} unread)
                        </span>
                      )}
                    </Paragraph>
                  </div>

                  {/* Part 4: Footer with timestamp, reply count, and action buttons */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Space size={12}>
                      <Text type="secondary" style={{ fontSize: '12px', color: '#8c8c8c' }}>
                        {formatTimestamp(notification.timestamp)}
                      </Text>
                      
                      {/* Reply count badge and thread toggle */}
                      {notification.threadId && (
                        <Button
                          type="text"
                          size="small"
                          icon={<MessageOutlined style={{ fontSize: '12px' }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            // Mark entire thread as read when expanding (not when collapsing)
                            const isExpanding = !expandedThreads.has(notification.threadId!);
                            if (isExpanding && !notification.isRead && notification.threadId) {
                              markThreadAsReadMutation.mutate({ threadId: notification.threadId });
                            }
                            toggleThread(notification.threadId);
                          }}
                          style={{
                            fontSize: '12px',
                            color: notification.replyCount && notification.replyCount > 0 ? '#1890ff' : '#8c8c8c',
                            padding: '2px 6px',
                            height: 'auto'
                          }}
                        >
                          {notification.replyCount && notification.replyCount > 0 
                            ? `${notification.replyCount} ${notification.replyCount === 1 ? 'reply' : 'replies'}`
                            : 'Reply'
                          }
                        </Button>
                      )}
                    </Space>
                    
                    {/* Only show View Details if URL points to a meaningful page (not Dashboard) */}
                    {isMeaningfulUrl(notification.url) && (
                      <Button
                        type="primary"
                        size="small"
                        style={{
                          fontSize: '12px',
                          fontWeight: 500,
                          padding: '4px 8px',
                          borderRadius: '4px',
                          height: '24px',
                          lineHeight: '1'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsRead(notification.id);
                          setDrawerVisible(false);
                          if (notification.url) {
                            navigateToUrl(notification.url);
                          }
                        }}
                      >
                        View Details →
                      </Button>
                    )}
                  </div>

                  {/* Thread View - Expanded */}
                  {notification.threadId && expandedThreads.has(notification.threadId) && (
                    <ThreadView
                      threadId={notification.threadId}
                      onClose={() => toggleThread(notification.threadId)}
                      isExpanded={true}
                    />
                  )}
                </div>
              </List.Item>
            );
          }}
          locale={{
            emptyText: (
              <div
                style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: '#8c8c8c'
                }}
              >
                <BellOutlined
                  style={{
                    fontSize: '64px',
                    color: '#d9d9d9',
                    marginBottom: '16px',
                    display: 'block'
                  }}
                />
                <Text type="secondary" style={{ fontSize: '16px', display: 'block', marginBottom: '8px' }}>
                  {showArchived ? 'No archived messages' : 'Your inbox is empty'}
                </Text>
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  {showArchived
                    ? 'Messages you clear will appear here'
                    : "You'll see messages here when they arrive"}
                </Text>
              </div>
            )
          }}
        />
        )}
      </Drawer>

      {/* Compose Message Modal */}
      <Modal
        title="Send Message"
        open={composeModalVisible}
        onCancel={() => setComposeModalVisible(false)}
        footer={null}
        width={500}
        destroyOnClose
      >
        <MessageComposer
          onClose={() => setComposeModalVisible(false)}
          onSuccess={() => {
            setComposeModalVisible(false);
            // Optionally refetch inbox after sending
            inboxQuery.refetch();
          }}
        />
      </Modal>
    </div>
  );
};

export default Notification;
